import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '@/i18n';
import type { TableColumn, BulkActionItem } from '@/components/ui/tables/Table';
import { TABLE_ACTION_VARIANT_COLOR } from '@/components/ui/tables/Table.constants';
import type { ActionMenuItem } from '@/components/ui/menus';
import { MonthlyCutConceptsCell, MonthlyCutProgressCell } from '@/components/features/monthlyCuts';
import {
  monthlyCutsService,
  monthlyCutsConceptGenerationService,
  CONCEPT_GENERATION_ACTIVE_CONFLICT_CODE,
} from '@/api/domains/attendance';
import type {
  MonthlyCutManagementItem,
  MonthlyCutsCounters,
  PayrollSubmissionSummary,
} from '@/api/domains/attendance';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult, PaginatedListSort } from '@/hooks/ui/usePaginatedList';
import { useHoldingCompanies } from '@/api/domains/catalog/holding/hooks/useHoldingCompanies';
import { monthlyCutsYearsService, mapMonthlyCutYearItemToOption } from '@/api/domains/catalog/monthlyCutsYears';
import { SPANISH_MONTHS } from '@/api/domains/attendance/mappers/monthlyCuts.mapper';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useCloseMonthlyCuts } from '@/hooks/attendance/monthlyCuts/useCloseMonthlyCuts';
import { useSyncMonthlyCutsRex } from '@/hooks/attendance/monthlyCuts/useSyncMonthlyCutsRex';
import { useExportPaymentConcepts } from '@/hooks/attendance/monthlyCuts/useExportPaymentConcepts';
import { ROUTE_DEFINITIONS } from '@/routes';
import { colors } from '@/assets/styles/colors';
import { getMonthOptions } from '@/utils/dateRange';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';


export interface MonthlyCutsFilters {
  companyId: string | null;
  year: string | null;
  month: string | null;
  state: string | null;
}

const initialFilters: MonthlyCutsFilters = {
  companyId: null,
  year: null,
  month: null,
  state: null,
};

export interface UseMonthlyCutsListResult {
  rows: MonthlyCutManagementItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<MonthlyCutManagementItem>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  searchText: string;
  sort: PaginatedListSort | undefined;
  loading: boolean;
  refetch: () => void;
  counters: MonthlyCutsCounters;
  countersLoading: boolean;
  filterConfig: SimpleFilter[];
  filters: Record<string, string | string[] | undefined>;
  selectedFilters: MonthlyCutsFilters;
  onChangeFilter: (key: string, value: string | string[]) => void;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  permissionsLoaded: boolean;
  // Selección + acciones masivas (generación de conceptos — ASIS-4969)
  selectable: boolean;
  bulkActions: BulkActionItem[];
  onSelectionChange: (selected: MonthlyCutManagementItem[]) => void;
  // Key para forzar el reinicio de la selección de la tabla tras un envío (remonta la tabla).
  tableSelectionKey: string;
  // Envío a remuneraciones (ASIS-4971 / FE-012)
  isSendPayrollModalOpen: boolean;
  sendPayrollCuts: MonthlyCutManagementItem[];
  onSendPayrollModalClose: () => void;
  onSendPayrollSuccess: (summary: PayrollSubmissionSummary) => void;
  // Identificación visual de cortes no sincronizados con rex+ remuneraciones (ASIS-5282)
  getRowTextColor: (row: MonthlyCutManagementItem) => string | undefined;
  hasUnsynchronizedCuts: boolean;
}

// Estado de corte que impide editar (espejo de la regla backend CONFLICT_MONTHLY_CUT_CLOSED).
const CLOSED_STATE = 'Cerrado';

// Tope de cortes por operación masiva de sincronización con rex+ (ASIS-4945). Espejo de
// MonthlyCutsConstants.MAX_MONTHLY_CUTS_PER_REX_SYNC: el backend rechaza selecciones mayores, y la
// acción se deshabilita antes de llegar a un error de validación.
const MAX_REX_SYNC_ITEMS = 20;

export function useMonthlyCutsList(): UseMonthlyCutsListResult {
  const { showApiError, showSuccess, showWarning, openModal, openSidePanel } = useUI();
  const { t: translate } = useTranslation();
  const navigate = useNavigate();

  
  const [sessionGenerations, setSessionGenerations] =
    useState<Map<number, { key: number; active: boolean }>>(new Map());
  const [selectedRows, setSelectedRows] = useState<MonthlyCutManagementItem[]>([]);

  // Envío a remuneraciones (ASIS-4971): estado del diálogo y snapshot de cortes a enviar.
  const [isSendPayrollModalOpen, setIsSendPayrollModalOpen] = useState(false);
  const [sendPayrollCuts, setSendPayrollCuts] = useState<MonthlyCutManagementItem[]>([]);
  // Token para reiniciar la selección visual de la tabla (remonta la tabla al incrementarse).
  const [selectionVersion, setSelectionVersion] = useState(0);

  const { isLoaded: permissionsLoaded, canView, canEdit, canDelete } = usePagePermissions();

  // Permiso de la pantalla DESTINO de la columna "Conceptos" (ASIS-5435): sin lectura en Conceptos
  // de pago el ícono se muestra con su tooltip pero no navega, para no mandar al usuario a un 403.
  const { canView: canViewPaymentConcepts } = usePagePermissions(
    ROUTE_DEFINITIONS.PAYMENT_CONCEPTS.code,
  );

  const { exportConceptos, exportingCutIds } = useExportPaymentConcepts();

  // Cierre masivo (ASIS-4963): la disponibilidad de la integración de remuneraciones y de conceptos
  // generados ahora viaja por fila en el listado (payrollIntegrationActive / hasGeneratedConcepts),
  // por lo que ya no es necesario sondear la disponibilidad global de la empresa autenticada.
  const { closeCuts } = useCloseMonthlyCuts();

  // Sincronización manual con rex+ remuneraciones (ASIS-4945).
  const { syncCuts } = useSyncMonthlyCutsRex();

  // Redirect if permissions loaded and user cannot view
  useEffect(() => {
    if (permissionsLoaded && !canView) {
      void navigate(ROUTE_DEFINITIONS.DASHBOARD_ASISTENCIA.path, { replace: true });
    }
  }, [permissionsLoaded, canView, navigate]);

  // Catalogs
  const { companies } = useHoldingCompanies();
  const companyOptions = useMemo(
    () => companies.map((c) => ({ value: String(c.id), label: c.name })),
    [companies],
  );

  // Counters state
  const [counters, setCounters] = useState<MonthlyCutsCounters>({ total: 0, open: 0, closed: 0 });
  const [countersLoading, setCountersLoading] = useState(true);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<MonthlyCutsFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<MonthlyCutManagementItem> | null> => {
      const response = await monthlyCutsService.listManagement(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          sort: params.sort
            ? { key: params.sort.key, direction: params.sort.direction.toLowerCase() as 'asc' | 'desc' }
            : undefined,
          filterCompanyId: params.filters?.companyId,
          year: params.filters?.year,
          month: params.filters?.month,
          state: params.filters?.state,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        showApiError(response, translate('attendance:monthlyCutsList.messages.loadError'));
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  // Single source of truth for filter state: usePaginatedList owns `filters`.
  // The table re-fetches on change; counters and year options derive from the same `filters`.
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    sort,
    debouncedSearchText: searchText,
    refetch: refetchTable,
    filters,
    setFilters,
  } = usePaginatedList<MonthlyCutManagementItem, MonthlyCutsFilters>({
    fetchFn,
    initialFilters,
    initialSort: { key: 'monthlyCutId', direction: 'DESC' },
    orderByMap: {
      id: 'monthlyCutId',
      companyName: 'companyName',
      year: 'year',
      month: 'month',
      range: 'startDate',
      process: 'process',
      state: 'state',
      conceptsStatus: 'concepts',
    },
    deduplicate: false,
    enabled: permissionsLoaded && canView,
    screenCode: ROUTE_DEFINITIONS.CORTE_MENSUAL.code,
  });

  // Year options — reload when the company filter changes (derived from the single filter source)
  const [yearOptions, setYearOptions] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    void monthlyCutsYearsService
      .getYears(filters.companyId, controller.signal)
      .then((response) => {
        if (response.error?.code === 'ABORTED') return;
        setYearOptions(response.data?.map(mapMonthlyCutYearItemToOption) ?? []);
      });
    return () => { controller.abort(); };
  }, [filters.companyId]);

  // Counters load: derives directly from `filters`; accepts optional signal.
  const loadCounters = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (!permissionsLoaded || !canView) return;
      setCountersLoading(true);
      try {
        const response = await monthlyCutsService.getCounters(
          {
            filterCompanyId: filters.companyId,
            year: filters.year,
            month: filters.month,
            state: filters.state,
          },
          signal,
        );
        if (response.error?.code === 'ABORTED') return;
        if (!response.success || !response.data) {
          showApiError(response, translate('attendance:monthlyCutsList.messages.countersError'));
          return;
        }
        setCounters(response.data);
      } finally {
        setCountersLoading(false);
      }
    },
    [
      permissionsLoaded,
      canView,
      filters.companyId,
      filters.year,
      filters.month,
      filters.state,
      showApiError,
      translate,
    ],
  );

  // Re-fetch counters whenever loadCounters reference changes
  // (i.e., when permissions or company/year/month/state filters change)
  useEffect(() => {
    const controller = new AbortController();
    void loadCounters(controller.signal);
    return () => { controller.abort(); };
  }, [loadCounters]);

  // Combined refetch: refreshes both table rows and counters
  const refetch = useCallback(() => {
    refetchTable();
    void loadCounters();
  }, [refetchTable, loadCounters]);

  const onChangeFilter = useCallback(
    (key: string, value: string | string[]) => {
      const strValue = Array.isArray(value) ? (value[0] ?? '') : value;
      setFilters((prev) => {
        const next = { ...prev, [key]: strValue || null };
        // Year options depend on the selected company; reset the dependent year filter
        // when the company changes so a stale year is not sent to the backend.
        if (key === 'companyId') {
          next.year = null;
        }
        return next;
      });
    },
    [setFilters],
  );

  const handleEdit = useCallback(
    (row: MonthlyCutManagementItem) => {
      void navigate(
        ROUTE_DEFINITIONS.CORTE_MENSUAL_EDITAR.path.replace(':id', String(row.id)),
      );
    },
    [navigate],
  );

  // Columna "Conceptos" (ASIS-5435): navega a Conceptos de pago con empresa, año, mes y corte ya
  // aplicados. El mes del listado es el nombre en español y la pantalla destino usa el mes
  // numérico, de ahí la traducción con SPANISH_MONTHS (no se deriva de startDate: un corte puede
  // abarcar dos meses, p.ej. 26/07–25/08 pertenece a "Agosto").
  const handleViewConcepts = useCallback(
    (row: MonthlyCutManagementItem) => {
      const params = new URLSearchParams({
        companyId: String(row.companyId),
        year: String(row.year),
        monthlyCutId: String(row.id),
      });
      const monthNumber = SPANISH_MONTHS[row.month];
      if (monthNumber !== undefined) params.set('month', String(monthNumber));

      void navigate(`${ROUTE_DEFINITIONS.PAYMENT_CONCEPTS.path}?${params.toString()}`);
    },
    [navigate],
  );

  // Ver detalle (ASIS-4964): panel lateral de solo lectura. Disponible con permiso de lectura, por
  // lo que no debe llevar a la pantalla de edición.
  const handleViewDetail = useCallback(
    (row: MonthlyCutManagementItem) => {
      openSidePanel({
        title: translate('attendance:monthlyCutDetail.title'),
        component: 'MonthlyCutDetailPanel',
        props: { cut: row },
      });
    },
    [openSidePanel, translate],
  );

  // Encola la generación de conceptos para uno o varios cortes. Al recibir 202, marca los
  // cortes efectivamente encolados como activos para que su fila muestre la barra de progreso.
  // Los cortes que ya estaban en curso (alreadyActiveCutIds) se informan sin bloquear el resto.
  const handleGenerateConcepts = useCallback(
    async (monthlyCutIds: number[]): Promise<void> => {
      if (monthlyCutIds.length === 0) return;

      const response = await monthlyCutsConceptGenerationService.start({ monthlyCutIds });

      if (!response.success || !response.data) {
        showApiError(
          response,
          translate('attendance:monthlyCutsList.conceptGeneration.messages.startError'),
        );
        // 409: todos los cortes solicitados ya están generando. Refrescamos el listado para que sus
        // filas muestren la barra (estado autoritativo conceptGenerationActive), en vez de dejar
        // solo el toast de error.
        if (response.error?.code === CONCEPT_GENERATION_ACTIVE_CONFLICT_CODE) {
          refetch();
        }
        return;
      }

      const { queuedCutIds, alreadyActiveCutIds } = response.data;
      if (queuedCutIds.length > 0) {
        // Marca cada corte encolado como activo en sesión e incrementa su `key` para (re)arrancar
        // la barra, incluso si ya se había generado antes en esta misma sesión (re-disparo).
        setSessionGenerations((previous) => {
          const next = new Map(previous);
          queuedCutIds.forEach((id) => {
            const prev = next.get(id);
            next.set(id, { key: (prev?.key ?? 0) + 1, active: true });
          });
          return next;
        });
      }

      // Si el lote traía cortes ya en curso, se informa (mensaje parcial) y se refresca el listado
      // para que esas filas muestren su barra con el estado autoritativo. Si no, éxito normal.
      if (alreadyActiveCutIds.length > 0) {
        showSuccess(
          translate('attendance:monthlyCutsList.conceptGeneration.messages.startPartialAlreadyActive', {
            queued: queuedCutIds.length,
            skipped: alreadyActiveCutIds.length,
          }),
        );
        refetch();
      } else if (queuedCutIds.length > 0) {
        showSuccess(translate('attendance:monthlyCutsList.conceptGeneration.messages.startSuccess'));
      }
      // Limpia la selección tras encolar para evitar reenvíos accidentales.
      setSelectedRows([]);
    },
    [showApiError, showSuccess, translate, refetch],
  );

  // Cierra la selección de cortes Abiertos. El resumen (cerrados/omitidos/no encontrados) se
  // informa por toast dentro de useCloseMonthlyCuts; aquí solo se limpia la selección y refresca.
  const handleCloseCuts = useCallback(
    async (monthlyCutIds: number[]): Promise<void> => {
      if (monthlyCutIds.length === 0) return;
      const summary = await closeCuts(monthlyCutIds);
      if (!summary) return;
      setSelectedRows([]);
      setSelectionVersion((version) => version + 1);
      refetch();
    },
    [closeCuts, refetch],
  );

  // Cierre unitario desde el menú de la fila (ASIS-4964). El cierre es irreversible, así que pasa
  // por confirmación explícita antes de invocar el mismo flujo que el cierre masivo.
  const handleCloseSingle = useCallback(
    (row: MonthlyCutManagementItem) => {
      openModal('ConfirmModal', {
        variant: 'error',
        icon: 'ban',
        title: translate('attendance:monthlyCutsList.closure.close'),
        bodyText: translate('attendance:monthlyCutsList.closure.single.confirmBody', {
          id: row.id,
        }),
        confirmText: translate('attendance:monthlyCutsList.closure.close'),
        onConfirm: async () => { await handleCloseCuts([row.id]); },
      });
    },
    [openModal, translate, handleCloseCuts],
  );

  // Cierre masivo desde la acción de la selección (ASIS-4970). Igual que el unitario, pasa por
  // confirmación explícita antes de disparar el cierre; el conteo cubre solo los cortes Abiertos
  // de la selección y, si hubo cortes Cerrados omitidos, el mismo texto lo informa (sin listarlos).
  const handleCloseBulk = useCallback(
    (openRows: MonthlyCutManagementItem[], skippedCount: number) => {
      openModal('ConfirmModal', {
        variant: 'error',
        icon: 'ban',
        title: translate('attendance:monthlyCutsList.closure.bulk.confirmTitle'),
        bodyText: skippedCount > 0
          ? translate('attendance:monthlyCutsList.closure.bulk.confirmBodySkipped', {
            count: openRows.length,
            skipped: skippedCount,
          })
          : translate('attendance:monthlyCutsList.closure.bulk.confirmBody', {
            count: openRows.length,
          }),
        confirmText: translate('attendance:monthlyCutsList.closure.bulk.confirmAction'),
        onConfirm: async () => { await handleCloseCuts(openRows.map((row) => row.id)); },
      });
    },
    [openModal, translate, handleCloseCuts],
  );

  // Sincroniza con rex+ los cortes indicados (ASIS-4945). El resumen (sincronizados / no
  // procesados / con error) se informa por toast dentro de useSyncMonthlyCutsRex; aquí solo se
  // limpia la selección y se refresca el listado para que las filas sincronizadas dejen de
  // mostrarse en rojo y muestren su número de proceso.
  const handleSyncCuts = useCallback(
    async (monthlyCutIds: number[]): Promise<void> => {
      if (monthlyCutIds.length === 0) return;
      const summary = await syncCuts(monthlyCutIds);
      if (!summary) return;
      setSelectedRows([]);
      setSelectionVersion((version) => version + 1);
      refetch();
    },
    [syncCuts, refetch],
  );

  // Sincronización masiva: a diferencia de la unitaria pasa por confirmación explícita, porque
  // actúa sobre varios cortes y crea procesos en un sistema externo. El conteo cubre solo los
  // cortes elegibles de la selección y, si hubo omitidos, el mismo texto lo informa.
  const handleSyncBulk = useCallback(
    (eligibleRows: MonthlyCutManagementItem[], skippedCount: number) => {
      openModal('ConfirmModal', {
        variant: 'important',
        icon: 'sync',
        title: translate('attendance:monthlyCutsList.payrollSync.bulk.confirmTitle'),
        bodyText: skippedCount > 0
          ? translate('attendance:monthlyCutsList.payrollSync.bulk.confirmBodySkipped', {
            count: eligibleRows.length,
            skipped: skippedCount,
          })
          : translate('attendance:monthlyCutsList.payrollSync.bulk.confirmBody', {
            count: eligibleRows.length,
          }),
        confirmText: translate('attendance:monthlyCutsList.payrollSync.bulk.confirmAction'),
        onConfirm: async () => { await handleSyncCuts(eligibleRows.map((row) => row.id)); },
      });
    },
    [openModal, translate, handleSyncCuts],
  );

  const onSelectionChange = useCallback(
    (selected: MonthlyCutManagementItem[]) => { setSelectedRows(selected); },
    [],
  );

  // Abre el diálogo de envío a remuneraciones con un snapshot de los cortes (fila o selección).
  const openSendPayrollModal = useCallback((cuts: MonthlyCutManagementItem[]) => {
    setSendPayrollCuts(cuts);
    setIsSendPayrollModalOpen(true);
  }, []);

  const onSendPayrollModalClose = useCallback(() => {
    setIsSendPayrollModalOpen(false);
  }, []);

  // Tras un envío: limpia la selección (remonta la tabla) y refresca listado + contadores. No
  // cierra el diálogo — la fase de resultado permanece visible hasta que el usuario la cierra.
  const onSendPayrollSuccess = useCallback(
    (_summary: PayrollSubmissionSummary) => {
      setSelectedRows([]);
      setSelectionVersion((version) => version + 1);
      refetch();
    },
    [refetch],
  );

  const tableSelectionKey = useMemo(() => String(selectionVersion), [selectionVersion]);

  
  const handleGenerationFinished = useCallback(
    (monthlyCutId: number) => {
      setSessionGenerations((previous) => {
        const prev = previous.get(monthlyCutId);
        if (!prev || !prev.active) return previous;
        const next = new Map(previous);
        next.set(monthlyCutId, { key: prev.key, active: false });
        return next;
      });
      refetch();
    },
    [refetch],
  );

  // Cortes no sincronizados con Remuneraciones Rex (ASIS-5282): el backend resuelve la regla
  // (empresa con integración activa + corte sin proceso asignado) y aquí solo se traduce a color de
  // fila y a la visibilidad de la leyenda, que depende de las filas de la página visible.
  const getRowTextColor = useCallback(
    (row: MonthlyCutManagementItem): string | undefined =>
      row.payrollNotSynchronized ? colors.feedback.error300 : undefined,
    [],
  );

  const hasUnsynchronizedCuts = useMemo(
    () => rows.some((row) => row.payrollNotSynchronized),
    [rows],
  );

  const monthOptions = useMemo(() => getMonthOptions(translate), [translate]);

  const stateOptions = useMemo(
    () => [
      { value: 'Abierto', label: translate('attendance:monthlyCutsList.filters.stateOpen') },
      { value: 'Cerrado', label: translate('attendance:monthlyCutsList.filters.stateClosed') },
    ],
    [translate],
  );

  const allOption = useMemo(
    () => ({ value: '', label: translate('attendance:monthlyCutsList.filters.all') }),
    [translate],
  );

  const filterConfig: SimpleFilter[] = useMemo(
    () => [
      {
        key: 'companyId',
        label: translate('attendance:monthlyCutsList.filters.company'),
        options: [allOption, ...companyOptions],
      },
      {
        key: 'year',
        label: translate('attendance:monthlyCutsList.filters.year'),
        options: [allOption, ...yearOptions],
      },
      {
        key: 'month',
        label: translate('attendance:monthlyCutsList.filters.month'),
        options: [allOption, ...monthOptions],
      },
      {
        key: 'state',
        label: translate('attendance:monthlyCutsList.filters.state'),
        options: [allOption, ...stateOptions],
      },
    ],
    [translate, allOption, companyOptions, yearOptions, monthOptions, stateOptions],
  );

  // Values for FilterDropdown derived from the single filter source (null → '' = "Todos")
  const filterValues = useMemo<Record<string, string>>(
    () => ({
      companyId: filters.companyId ?? '',
      year: filters.year ?? '',
      month: filters.month ?? '',
      state: filters.state ?? '',
    }),
    [filters],
  );

  // Elegibilidad de la selección para las acciones masivas (ASIS-4963). Los cortes Cerrados nunca
  // son elegibles para ninguna acción; "Enviar a remuneraciones" exige además conceptos generados
  // y una empresa con integración activa (ambos datos viajan por fila desde el listado).
  const openSelected = useMemo(
    () => selectedRows.filter((row) => row.state !== CLOSED_STATE),
    [selectedRows],
  );
  const closedSkippedCount = selectedRows.length - openSelected.length;
  const payrollEligible = useMemo(
    () => openSelected.filter((row) => row.hasGeneratedConcepts && row.payrollIntegrationActive),
    [openSelected],
  );
  const payrollNoConceptsCount = useMemo(
    () => openSelected.filter((row) => !row.hasGeneratedConcepts).length,
    [openSelected],
  );
  const payrollNoIntegrationCount = useMemo(
    () => openSelected.filter(
      (row) => row.hasGeneratedConcepts && !row.payrollIntegrationActive,
    ).length,
    [openSelected],
  );

  // Elegibilidad para "Sincronizar" (ASIS-4945): el backend ya resuelve la regla completa
  // (empresa con integración rex+ activa y corte sin proceso) en payrollNotSynchronized, el mismo
  // campo que pinta la fila en rojo. Los cortes Cerrados sí son elegibles.
  const rexSyncEligible = useMemo(
    () => selectedRows.filter((row) => row.payrollNotSynchronized),
    [selectedRows],
  );
  const rexSyncSkippedCount = selectedRows.length - rexSyncEligible.length;
  const rexSyncExceedsLimit = rexSyncEligible.length > MAX_REX_SYNC_ITEMS;

  // Motivo por el que "Sincronizar" está deshabilitada, para un tooltip específico.
  const rexSyncDisabledTooltip = useMemo(() => {
    if (rexSyncEligible.length === 0) {
      return translate('attendance:monthlyCutsList.payrollSync.bulk.disabledNoneEligible');
    }
    if (rexSyncExceedsLimit) {
      return translate('attendance:monthlyCutsList.payrollSync.bulk.disabledTooMany', {
        max: MAX_REX_SYNC_ITEMS,
      });
    }
    return undefined;
  }, [rexSyncEligible, rexSyncExceedsLimit, translate]);

  // Motivo por el que "Enviar a remuneraciones" está deshabilitado, para un tooltip específico.
  const payrollDisabledTooltip = useMemo(() => {
    if (payrollEligible.length > 0) return undefined;
    if (openSelected.length === 0) {
      return translate('attendance:monthlyCutsSendToPayroll.bulk.disabledAllClosed');
    }
    if (payrollNoConceptsCount === openSelected.length) {
      return translate('attendance:monthlyCutsSendToPayroll.bulk.disabledNoConcepts');
    }
    return translate('attendance:monthlyCutsSendToPayroll.bulk.disabledNoIntegration');
  }, [payrollEligible, openSelected, payrollNoConceptsCount, translate]);

  // Motivo compartido por "Generar conceptos" y "Cerrar corte": ambas exigen al menos un corte
  // Abierto en la selección, por lo que usan la misma clave neutral del listado.
  const allClosedTooltip = translate('attendance:monthlyCutsList.bulk.disabledAllClosed');

  // Acciones masivas. Generar conceptos y Enviar a remuneraciones exigen permiso WRITE; Cerrar
  // exige DELETE, igual que el endpoint POST management/closures y que la opción unitaria del menú
  // (ASIS-4964). Cada acción se ejecuta únicamente sobre su subconjunto elegible de la selección;
  // cuando algo se omite, se informa con un toast antes de disparar la acción.
  const bulkActions: BulkActionItem[] = useMemo(() => {
    const actions: BulkActionItem[] = [];

    if (canEdit) {
      actions.push(
        {
          label: translate('attendance:monthlyCutsList.conceptGeneration.menu.generate'),
          icon: 'calculator',
          disabled: openSelected.length === 0,
          tooltip: openSelected.length === 0 ? allClosedTooltip : undefined,
          onClick: () => {
            if (closedSkippedCount > 0) {
              showWarning(
                translate('attendance:monthlyCutsList.conceptGeneration.messages.bulkSkippedClosed', {
                  count: closedSkippedCount,
                }),
              );
            }
            void handleGenerateConcepts(openSelected.map((row) => row.id));
          },
        },
        {
          label: translate('attendance:monthlyCutsSendToPayroll.bulk.send'),
          icon: 'paperPlane',
          disabled: payrollEligible.length === 0,
          tooltip: payrollDisabledTooltip,
          onClick: () => {
            const skippedCount = selectedRows.length - payrollEligible.length;
            if (skippedCount > 0) {
              showWarning(
                translate('attendance:monthlyCutsSendToPayroll.bulk.skippedSummary', {
                  closed: closedSkippedCount,
                  noConcepts: payrollNoConceptsCount,
                  noIntegration: payrollNoIntegrationCount,
                }),
              );
            }
            openSendPayrollModal(payrollEligible);
          },
        },
      );
    }

    if (canDelete) {
      actions.push({
        label: translate('attendance:monthlyCutsList.closure.close'),
        icon: 'ban',
        disabled: openSelected.length === 0,
        tooltip: openSelected.length === 0 ? allClosedTooltip : undefined,
        onClick: () => { handleCloseBulk(openSelected, closedSkippedCount); },
      });
    }

    // Sincronización masiva con rex+ (ASIS-4945): va al final de la barra de acciones. Exige
    // permiso de modificar, igual que el endpoint POST management/rex-synchronizations y que la
    // opción unitaria del menú de la fila.
    if (canEdit) {
      actions.push({
        label: translate('attendance:monthlyCutsList.payrollSync.bulk.sync'),
        icon: 'sync',
        disabled: rexSyncEligible.length === 0 || rexSyncExceedsLimit,
        tooltip: rexSyncDisabledTooltip,
        onClick: () => { handleSyncBulk(rexSyncEligible, rexSyncSkippedCount); },
      });
    }

    return actions;
  }, [
    canEdit,
    canDelete,
    translate,
    selectedRows,
    openSelected,
    closedSkippedCount,
    allClosedTooltip,
    handleCloseBulk,
    handleGenerateConcepts,
    payrollEligible,
    payrollDisabledTooltip,
    payrollNoConceptsCount,
    payrollNoIntegrationCount,
    openSendPayrollModal,
    showWarning,
    rexSyncEligible,
    rexSyncExceedsLimit,
    rexSyncSkippedCount,
    rexSyncDisabledTooltip,
    handleSyncBulk,
  ]);

  const columns: TableColumn<MonthlyCutManagementItem>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('attendance:monthlyCutsList.fields.id'),
        sortable: true,
        render: (row) => row.id,
      },
      {
        key: 'companyName',
        header: translate('attendance:monthlyCutsList.fields.company'),
        sortable: true,
        render: (row) => row.companyName,
      },
      {
        key: 'year',
        header: translate('attendance:monthlyCutsList.fields.year'),
        sortable: true,
        render: (row) => row.year,
      },
      {
        key: 'month',
        header: translate('attendance:monthlyCutsList.fields.month'),
        sortable: true,
        render: (row) => row.month,
      },
      {
        key: 'range',
        header: translate('attendance:monthlyCutsList.fields.range'),
        sortable: true,
        render: (row) => row.range,
      },
      {
        key: 'process',
        header: translate('attendance:monthlyCutsList.fields.process'),
        sortable: true,
        render: (row) => row.process ?? '-',
      },
      {
        key: 'state',
        header: translate('attendance:monthlyCutsList.fields.state'),
        sortable: true,
        render: (row) => row.state,
      },
      {
        key: 'conceptsStatus',
        header: translate('attendance:monthlyCutsList.fields.concepts'),
        sortable: true,
        align: 'center',
        render: (row) =>
          React.createElement(MonthlyCutConceptsCell, {
            status: row.conceptsStatus,
            onNavigate: canViewPaymentConcepts ? () => handleViewConcepts(row) : undefined,
          }),
      },
      {
        key: 'actions',
        header: '',
        sortable: false,
        // Sin ancho explícito: usa el ancho responsivo del resto de columnas (no fuerza scroll
        // horizontal en notebooks). La barra llena el ancho de la celda y queda centrada; el menú
        // "…" se centra igual que en el resto de la app.
        align: 'center',
        overflowStrategy: 'wrap',
        render: (row) => {
          // Visibilidad y habilitación del menú (ASIS-4964). Dos mecanismos distintos y no
          // intercambiables: los permisos y la no aplicabilidad OCULTAN la opción; las reglas de
          // negocio sobre una acción que el usuario sí podría ejecutar la DESHABILITAN con un
          // tooltip que explica el motivo.
          const isClosed = row.state === CLOSED_STATE;
          const closedTooltip = translate('attendance:monthlyCutsList.menu.disabledClosed');
          const menuItems: ActionMenuItem[] = [];

          // Ver detalle: solo requiere permiso de lectura; el panel no permite editar nada.
          if (canView) {
            menuItems.push({
              id: 'view',
              label: translate('common:tableActions.detail'),
              icon: 'eye',
              color: TABLE_ACTION_VARIANT_COLOR.default,
              onClick: () => handleViewDetail(row),
            });
          }

          if (canEdit) {
            menuItems.push(
              {
                id: 'edit',
                label: translate('common:tableActions.edit'),
                icon: 'edit',
                color: TABLE_ACTION_VARIANT_COLOR.default,
                onClick: () => handleEdit(row),
                disabled: isClosed,
                tooltip: isClosed ? closedTooltip : undefined,
              },
              {
                // Mismo icono y color que la acción masiva equivalente, para que se reconozcan como
                // la misma operación en sus dos variantes (unitaria y sobre la selección).
                id: 'generateConcepts',
                label: translate('attendance:monthlyCutsList.conceptGeneration.menu.generate'),
                icon: 'calculator',
                color: TABLE_ACTION_VARIANT_COLOR.default,
                onClick: () => { void handleGenerateConcepts([row.id]); },
                disabled: isClosed,
                tooltip: isClosed ? closedTooltip : undefined,
              },
            );
          }

          // "Enviar a remuneraciones" no aplica sin conceptos generados, así que se oculta. Con
          // conceptos generados sí se ofrece, deshabilitada cuando el corte está cerrado o cuando
          // la empresa del corte no tiene integración activa (`payrollIntegrationActive` viaja por
          // fila desde el listado, sin sondeos adicionales). Con ambos motivos presentes prevalece
          // el del corte cerrado, que es el estado más determinante.
          if (canEdit && row.hasGeneratedConcepts) {
            const payrollDisabled = isClosed || !row.payrollIntegrationActive;
            menuItems.push({
              id: 'sendToPayroll',
              label: translate('attendance:monthlyCutsSendToPayroll.menu.send'),
              icon: 'export',
              color: TABLE_ACTION_VARIANT_COLOR.default,
              onClick: () => { openSendPayrollModal([row]); },
              disabled: payrollDisabled,
              tooltip: isClosed
                ? closedTooltip
                : (!row.payrollIntegrationActive
                  ? translate('attendance:monthlyCutsSendToPayroll.menu.notAvailableTooltip')
                  : undefined),
            });
          }

          // "Exportar conceptos": descarga de lectura (el endpoint exige READ), por lo que basta
          // permiso de lectura. Se oculta sin conceptos generados —no hay nada que exportar— y se
          // deshabilita mientras la descarga de ESTA fila está en vuelo; las otras filas siguen
          // disponibles. Un corte cerrado sí puede exportarse.
          if (canView && row.hasGeneratedConcepts) {
            menuItems.push({
              id: 'exportConceptos',
              label: translate('attendance:monthlyCutsList.conceptsExport.menu.export'),
              icon: 'import',
              color: TABLE_ACTION_VARIANT_COLOR.default,
              onClick: () => { void exportConceptos(row.id); },
              disabled: exportingCutIds.has(row.id),
            });
          }

          // "Cerrar": exige permiso de eliminar (igual que el endpoint) y se oculta en un corte ya
          // cerrado, donde la acción no tiene efecto posible.
          if (canDelete && !isClosed) {
            menuItems.push({
              id: 'close',
              label: translate('attendance:monthlyCutsList.closure.close'),
              icon: 'ban',
              color: TABLE_ACTION_VARIANT_COLOR.default,
              onClick: () => handleCloseSingle(row),
            });
          }

          // "Sincronizar" (ASIS-4945): va al final del menú. Solo aplica a un corte no
          // sincronizado de una empresa con integración rex+ activa; en cualquier otro caso (ya
          // tiene proceso o la empresa no tiene integración) la acción no es aplicable y se oculta.
          // Un corte cerrado sí puede sincronizarse, porque la operación no altera datos de
          // asistencia. Exige permiso de modificar, igual que el endpoint.
          if (canEdit && row.payrollNotSynchronized) {
            menuItems.push({
              id: 'syncRex',
              label: translate('attendance:monthlyCutsList.payrollSync.menu.sync'),
              icon: 'sync',
              color: TABLE_ACTION_VARIANT_COLOR.default,
              onClick: () => { void handleSyncCuts([row.id]); },
            });
          }

          // La celda decide entre barra de progreso, mensaje de error y menú "…". La barra arranca
          // por la señal de sesión (recién disparada aquí) o por el estado autoritativo del listado
          // (generación en curso al cargar / de otro usuario / tras recargar).
          const session = sessionGenerations.get(row.id);
          return React.createElement(MonthlyCutProgressCell, {
            monthlyCutId: row.id,
            menuItems,
            sessionActive: session?.active ?? false,
            generationKey: session?.key ?? 0,
            initiallyGenerating: row.conceptGenerationActive,
            initialProgressPercentage: row.conceptGenerationProgress,
            onFinished: () => handleGenerationFinished(row.id),
          });
        },
      },
    ],
    [
      translate,
      canView,
      canEdit,
      canDelete,
      canViewPaymentConcepts,
      handleViewConcepts,
      handleViewDetail,
      handleEdit,
      handleGenerateConcepts,
      handleCloseSingle,
      sessionGenerations,
      handleGenerationFinished,
      openSendPayrollModal,
      exportConceptos,
      exportingCutIds,
      handleSyncCuts,
    ],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    onSearch,
    onSortChange,
    searchText,
    sort,
    loading,
    refetch,
    counters,
    countersLoading,
    filterConfig,
    filters: filterValues,
    selectedFilters: filters,
    onChangeFilter,
    canView,
    canEdit,
    canDelete,
    permissionsLoaded,
    selectable: canEdit || canDelete,
    bulkActions,
    onSelectionChange,
    tableSelectionKey,
    isSendPayrollModalOpen,
    sendPayrollCuts,
    onSendPayrollModalClose,
    onSendPayrollSuccess,
    getRowTextColor,
    hasUnsynchronizedCuts,
  };
}

export default useMonthlyCutsList;
